import { useSelector } from "react-redux";
import { useRef, useMemo, useState, useEffect, MouseEventHandler, ChangeEventHandler } from "react";
import dayjs from "dayjs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { Asset } from "app/types";
import { RootState } from "app/store";
import { usePickImageAsset } from "app/hook";
import I18n from "common/i18n";
import { ImagePreview } from "common/elements";
import { btnDelay, usePlaySound, formatStorageUsage } from "common/utils";
import { useAssetLimit } from "features/subscription";
import { StockTabIndex } from "features/creator/types";
import { AIGenerateOnlyModal } from "features/generation";
import { getUserAssets, AssetsResponse } from "features/user/api";

export const UserStock = ({ selected, tab }: { selected: boolean; tab: StockTabIndex }) => {
  const play = usePlaySound();
  const queryClient = useQueryClient();
  const ref = useRef<HTMLInputElement>();
  const pickImageAsset = usePickImageAsset();
  const inputRef = useRef<HTMLInputElement>();
  const [preview, setPreview] = useState(false);
  const [selectedAsset, setSelectAsset] = useState<Asset>(null);

  useEffect(() => {
    setSelectAsset(null);
  }, [tab]);
  const [showAIModal, setShowAIModal] = useState(false);
  const user = useSelector((state: RootState) => state.user.active);
  const assetLimit = useAssetLimit();
  const isUnlimited = assetLimit.unlimited;
  const limitValue = assetLimit.limit;
  const usedValue = assetLimit.used;
  const isOverLimit = !isUnlimited && usedValue > limitValue;
  const { data } = useQuery<AssetsResponse>({
    queryKey: ["creator/getUserAssets", user.uid],
    queryFn: () => getUserAssets({ uid: user.uid }),
    staleTime: Infinity,
    placeholderData: { assets: [], totalSize: 0 },
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const assets = Array.isArray(data?.assets) ? data.assets : [];

  const isAIAsset = (asset: Asset) => asset.tags?.startsWith("ai_generated:");

  const target = useMemo(
    () =>
      assets.filter((a) => {
        if (tab === StockTabIndex.AI) return isAIAsset(a);
        if (tab === StockTabIndex.DRAWING) return /.svg/.test(a.url) && !isAIAsset(a);
        return !/.svg/.test(a.url) && !isAIAsset(a);
      }),
    [assets, tab],
  );

  const pickImage: ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files[0];
    if (file) {
      await pickImageAsset(
        file,
        user.uid,
        async () => {
          inputRef.current.value = "";
        },
        () => {
          inputRef.current.value = "";
        },
      );
    }
  };

  const handleSelectAsset =
    (asset: Asset): MouseEventHandler<HTMLImageElement> =>
    async (event) => {
      play();
      event.stopPropagation();
      setSelectAsset(asset);
      setPreview(true);
    };

  const handleUpdateAsset = (asset: Asset) => {
    setSelectAsset(asset);
    if (queryClient.getQueryData(["creator/getUserAssets", user.uid])) {
      queryClient.setQueryData<AssetsResponse>(["creator/getUserAssets", user.uid], (old) =>
        old
          ? {
              ...old,
              assets: old.assets.map((a) => (a.id === asset.id ? asset : a)),
            }
          : { assets: [], totalSize: 0 },
      );
    }
  };

  const handleClose = () => {
    btnDelay(() => {
      play();
      setPreview(false);
      setSelectAsset(null);
    });
  };

  const handleOpenAIModal = () => {
    btnDelay(() => {
      play();
      setShowAIModal(true);
    });
  };

  const handleCloseAIModal = () => {
    setShowAIModal(false);
  };

  const TitleComponent = ({ title }) => (
    <div className="w-full h-[32px] bg-beige-alt flex-col-el justify-center items-start rounded-t-xl">
      <p className="text-body leading-[30px] text-white !ml-[16px]">
        {title}
        {!isUnlimited && (
          <span className={`${isOverLimit ? "text-red-100" : "text-white"} ml-2`}>
            {formatStorageUsage(usedValue, limitValue, isUnlimited)}
          </span>
        )}
      </p>
    </div>
  );

  return (
    <div
      className="flex-col-center w-full h-full p-3"
      style={{ display: selected ? undefined : "none" }}
    >
      <div className="flex-row-el relative w-full h-full items-start">
        <ImagePreview
          asset={selectedAsset}
          limit={false}
          visible={preview}
          readOnly={false}
          update={handleUpdateAsset}
          close={handleClose}
        />

        <div className="flex-col-el flex-center w-full h-full">
          <TitleComponent
            title={
              tab === StockTabIndex.ALBUM
                ? I18n.t("MSG_ASSET_ALBUM_TITLE")
                : tab === StockTabIndex.DRAWING
                  ? I18n.t("MSG_ASSET_DRAWING_TITLE")
                  : I18n.t("MSG_USER_STOCK_AI_TITLE")
            }
          />

          <div
            ref={ref}
            className="flex-col-el relative w-full h-full flex-1 bg-beige-20 rounded-b-xl visible-scrollbar"
          >
            <div className="flex-row-el flex-center shrink-0 w-full h-[90px]">
              <div className="flex-row-el flex-center p-2 bg-orange-alt rounded-3xl">
                <p className="text-body text-textcolor-black text-font-caption2">
                  {tab === StockTabIndex.ALBUM
                    ? I18n.t("MSG_ASSET_DRAWING_ADD_ALBUM_TITLE")
                    : tab === StockTabIndex.DRAWING
                      ? I18n.t("MSG_ASSET_DRAWING_ADD_WARNING_TITLE")
                      : I18n.t("MSG_USER_STOCK_AI_HINT")}
                </p>
              </div>
            </div>

            {isOverLimit && (
              <div className="flex-row-el flex-center w-full h-[48px] bg-orange-100 rounded-lg mb-2 mx-2">
                <span className="material-symbols-outlined !text-xl text-white mr-2">warning</span>
                <p className="text-body text-white text-font-caption2">
                  {I18n.t("MSG_ASSET_STORAGE_OVER_LIMIT")}
                </p>
              </div>
            )}

            <div className="grid grid-cols-asset-auto-fill w-full items-start justify-center gap-2 z-10">
              {tab === StockTabIndex.ALBUM && (
                <div className="flex-col-el flex-center shrink-0 w-full h-full upsilon gap-3">
                  <div className="btn btn-primary !p-0 !m-0 w-[160px] h-[160px] cursor-pointer bg-green-100 rounded-lg">
                    <div className="flex-col-center w-full h-full">
                      <input
                        ref={inputRef}
                        type="file"
                        onClick={() => play()}
                        onChange={pickImage}
                        accept="image/*"
                        className="absolute w-[160px] h-[160px] opacity-0 z-50 peer cursor-pointer"
                      />
                      <AddPhotoAlternateIcon sx={{ fontSize: 84, color: "white" }} />
                      <p className="text-body text-white text-font-size-body2">
                        {I18n.t("MSG_ASSET_ALBUM_ADD_TITLE")}
                      </p>
                    </div>
                  </div>
                  <div className="w-full h-[20px]" />
                </div>
              )}

              {tab === StockTabIndex.AI && (
                <div className="flex-col-el flex-center shrink-0 w-full h-full upsilon gap-3">
                  <div
                    onClick={handleOpenAIModal}
                    className="btn btn-primary !p-0 !m-0 w-[160px] h-[160px] cursor-pointer bg-blue-100 rounded-lg"
                  >
                    <div className="flex-col-center w-full h-full">
                      <span className="material-symbols-outlined text-white !text-[84px]">
                        auto_awesome
                      </span>
                      <p className="text-body text-white text-font-size-body2">
                        {I18n.t("MSG_AI_TAB_GENERATE")}
                      </p>
                    </div>
                  </div>
                  <div className="w-full h-[20px]" />
                </div>
              )}

              {target
                .sort((a, b) => (dayjs(a.created_at) < dayjs(b.created_at) ? 1 : -1))
                .map((asset, index) => (
                  <div key={asset.id} className="flex-col-el relative">
                    <div className="flex-col-el relative flex-center w-[160px] h-[160px] bg-beige-20 p-2 border-solid frame">
                      <img
                        alt="*"
                        loading={index > 16 ? "lazy" : "eager"}
                        onClick={handleSelectAsset(asset)}
                        className="w-full h-full object-contain bg-white"
                        src={asset.url}
                      />
                    </div>
                    <p className="text-body text-textcolor-black text-font-caption2 h-[20px] text-body-ellipsis text-center !px-5">
                      {asset.name}
                    </p>
                  </div>
                ))}
            </div>

            {target.length === 0 && (
              <div className="flex-col-el flex-center w-full h-full absolute z-10 pointer-events-none">
                <p className="text-body text-gray-80 text-font-caption2-narrow">
                  {tab === StockTabIndex.ALBUM
                    ? I18n.t("MSG_ASSET_ALBUM_EMPTY_MESSAGE")
                    : tab === StockTabIndex.DRAWING
                      ? I18n.t("MSG_ASSET_DRAWING_EMPTY_MESSAGE")
                      : I18n.t("MSG_USER_STOCK_AI_EMPTY")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <AIGenerateOnlyModal open={showAIModal} onClose={handleCloseAIModal} uid={user.uid} />
    </div>
  );
};
